import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, Plus, X, Check, Calendar, User, Wrench, 
  TrendingUp, ChevronLeft, ChevronRight, BarChart3,
  Edit, Trash2, FileText, Download
} from 'lucide-react';
import { vtEntriesAPI, techniciensAPI, ordresTravailAPI, exportAPI } from '../../api';
import { vtToHM } from '../../utils';

// ============================================================
// CONSTANTS
// ============================================================

const VT_TO_MIN = 5;

// Get week dates (Mon-Fri)
const getWeekDates = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const dates = [];
  for (let i = 0; i < 5; i++) {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    dates.push(dd.toISOString().split('T')[0]);
  }
  return dates;
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const SaisieVT = () => {
  // State
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [techniciens, setTechniciens] = useState([]);
  const [ots, setOts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Modal state
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    technicien_id: '',
    ordre_travail_id: '',
    date: new Date().toISOString().split('T')[0],
    vt_realises: 12,
    description: ''
  });

  const weekDates = getWeekDates(currentDate);
  const today = new Date().toISOString().split('T')[0];

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const fetchData = useCallback(async () => {
    const dates = getWeekDates(currentDate);
    setLoading(true);
    try {
      const [entriesRes, statsRes, techRes, otsRes] = await Promise.all([
        vtEntriesAPI.getAll({ date_from: dates[0], date_to: dates[4] }),
        vtEntriesAPI.getStats(dates[0], dates[4]),
        techniciensAPI.getAll(),
        ordresTravailAPI.getAll()
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setTechniciens(techRes.data.filter(t => t.actif !== false));
      setOts(otsRes.data.filter(o => !['termine', 'annule'].includes(o.statut)));
    } catch (err) {
      console.error('Error fetching VT data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const navWeek = (delta) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + delta * 7);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setCurrentDate(today);
  };

  const openNewEntry = (techId = '', date = today) => {
    setEditingEntry(null);
    setFormData({
      technicien_id: techId,
      ordre_travail_id: '',
      date: date,
      vt_realises: 12,
      description: ''
    });
    setShowEntryModal(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      technicien_id: entry.technicien_id,
      ordre_travail_id: entry.ordre_travail_id || '',
      date: entry.date,
      vt_realises: entry.vt_realises,
      description: entry.description || ''
    });
    setShowEntryModal(true);
  };

  const handleSaveEntry = async () => {
    try {
      if (editingEntry) {
        await vtEntriesAPI.update(editingEntry.id, formData);
      } else {
        await vtEntriesAPI.create(formData);
      }
      setShowEntryModal(false);
      fetchData();
    } catch (err) {
      alert('Erreur sauvegarde');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette saisie ?')) return;
    try {
      await vtEntriesAPI.delete(entryId);
      fetchData();
    } catch (err) {
      alert('Erreur suppression');
    }
  };

  // Get entries for a specific tech and date
  const getEntriesFor = (techId, date) => {
    return entries.filter(e => e.technicien_id === techId && e.date === date);
  };

  // Calculate VT total for tech and date
  const getVTTotal = (techId, date) => {
    const dayEntries = getEntriesFor(techId, date);
    return dayEntries.reduce((sum, e) => sum + e.vt_realises, 0);
  };

  // Get tech stats
  const getTechStats = (techId) => {
    return stats?.techniciens?.find(t => t.technicien_id === techId);
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)]" />
      </div>
    );
  }

  return (
    <div data-testid="saisie-vt-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            className="gcal-nav-btn" 
            onClick={() => navWeek(-1)}
            data-testid="vt-nav-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button 
            className="gcal-nav-btn"
            onClick={() => navWeek(1)}
            data-testid="vt-nav-next"
          >
            <ChevronRight size={18} />
          </button>
          <button 
            className="gcal-today-btn"
            onClick={goToToday}
            data-testid="vt-today-btn"
          >
            Aujourd'hui
          </button>
          <span className="text-lg font-bold">
            Semaine {getWeekNumber(weekDates[0])} — {new Date(weekDates[0]).toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}
          </span>
        </div>

        <div className="flex gap-2">
          <button 
            className="btn btn-ghost"
            onClick={async () => {
              try {
                const res = await exportAPI.techniciensVT(weekDates[0], weekDates[4]);
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `techniciens_vt_${weekDates[0]}_${weekDates[4]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (err) {
                alert('Erreur export CSV');
              }
            }}
            data-testid="export-vt-csv-btn"
          >
            <Download size={14} /> Export CSV
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => openNewEntry()}
            data-testid="new-vt-entry-btn"
          >
            <Plus size={14} /> Nouvelle saisie
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="kpi-realises">
          <div className="stat-label">VT Réalisés</div>
          <div className="stat-value" style={{ color: 'var(--accent)' }}>
            {stats?.totaux?.vt_realises || 0}
          </div>
          <div className="stat-sub">{vtToHM(stats?.totaux?.vt_realises || 0)}</div>
        </div>
        <div className="stat-card" data-testid="kpi-planifies">
          <div className="stat-label">VT Planifiés</div>
          <div className="stat-value" style={{ color: '#3b82f6' }}>
            {stats?.totaux?.vt_planifies || 0}
          </div>
          <div className="stat-sub">{vtToHM(stats?.totaux?.vt_planifies || 0)}</div>
        </div>
        <div className="stat-card" data-testid="kpi-rendement">
          <div className="stat-label">Rendement Global</div>
          <div className="stat-value" style={{ 
            color: (stats?.totaux?.rendement || 0) >= 100 ? '#22c55e' : 
                   (stats?.totaux?.rendement || 0) >= 80 ? '#f59e0b' : '#ef4444' 
          }}>
            {stats?.totaux?.rendement || 0}%
          </div>
          <div className="stat-sub">réalisés / planifiés</div>
        </div>
        <div className="stat-card" data-testid="kpi-saisies">
          <div className="stat-label">Saisies</div>
          <div className="stat-value" style={{ color: '#8b5cf6' }}>
            {entries.length}
          </div>
          <div className="stat-sub">cette semaine</div>
        </div>
      </div>

      {/* Main Grid - Technicians x Days */}
      <div className="card">
        <div className="card-header">
          <span className="card-title flex items-center gap-2">
            <Clock size={16} className="text-[var(--accent)]" />
            Saisie des temps réalisés
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-3 text-xs text-[var(--text3)] uppercase w-48">
                  Technicien
                </th>
                {weekDates.map((date, idx) => {
                  const d = new Date(date);
                  const isToday = date === today;
                  return (
                    <th 
                      key={date} 
                      className={`text-center p-3 text-xs uppercase ${isToday ? 'bg-[var(--accent-dim)]' : ''}`}
                    >
                      <div className={isToday ? 'text-[var(--accent)] font-bold' : 'text-[var(--text3)]'}>
                        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'][idx]}
                      </div>
                      <div className={`text-lg ${isToday ? 'text-[var(--accent)]' : ''}`}>
                        {d.getDate()}
                      </div>
                    </th>
                  );
                })}
                <th className="text-center p-3 text-xs text-[var(--text3)] uppercase w-24">
                  Total
                </th>
                <th className="text-center p-3 text-xs text-[var(--text3)] uppercase w-24">
                  Rendement
                </th>
              </tr>
            </thead>
            <tbody>
              {techniciens.filter(t => !t.role || t.role === 'technicien' || t.role === 'Atelier').map(tech => {
                const techStats = getTechStats(tech.id);
                const weekTotal = weekDates.reduce((sum, d) => sum + getVTTotal(tech.id, d), 0);
                const rendement = techStats?.rendement || 0;
                
                return (
                  <tr key={tech.id} className="border-b border-[var(--border)] hover:bg-[var(--bg3)]">
                    {/* Technician cell */}
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="tech-avatar"
                          style={{ background: tech.couleur_hex, width: 36, height: 36 }}
                        >
                          {tech.initiales}
                        </div>
                        <div>
                          <div className="font-medium">{tech.prenom} {tech.nom}</div>
                          <div className="text-xs text-[var(--text3)]">
                            Max: {tech.vt_jour_max || 96} VT/jour
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Day cells */}
                    {weekDates.map((date) => {
                      const dayEntries = getEntriesFor(tech.id, date);
                      const dayTotal = getVTTotal(tech.id, date);
                      const isToday = date === today;
                      
                      return (
                        <td 
                          key={date} 
                          className={`p-2 text-center ${isToday ? 'bg-[var(--accent-dim)]' : ''}`}
                        >
                          <div 
                            className="min-h-[60px] rounded-lg p-2 cursor-pointer transition-all hover:bg-[var(--bg3)] border border-transparent hover:border-[var(--border)]"
                            onClick={() => openNewEntry(tech.id, date)}
                            data-testid={`vt-cell-${tech.id}-${date}`}
                          >
                            {dayEntries.length > 0 ? (
                              <div className="space-y-1">
                                {dayEntries.map(entry => (
                                  <div 
                                    key={entry.id}
                                    className="text-xs p-1 rounded flex items-center justify-between group"
                                    style={{ background: 'var(--bg3)' }}
                                    onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }}
                                  >
                                    <span className="font-mono font-bold text-[var(--accent)]">
                                      {entry.vt_realises} VT
                                    </span>
                                    <button 
                                      className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-all"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry.id); }}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))}
                                <div className="text-xs text-[var(--text3)]">
                                  Total: {dayTotal}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full text-[var(--text3)] opacity-30 hover:opacity-60">
                                <Plus size={20} />
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    
                    {/* Week total */}
                    <td className="p-3 text-center">
                      <div className="font-mono font-bold text-lg">{weekTotal}</div>
                      <div className="text-xs text-[var(--text3)]">{vtToHM(weekTotal)}</div>
                    </td>
                    
                    {/* Rendement */}
                    <td className="p-3 text-center">
                      <div 
                        className="font-bold text-lg"
                        style={{ 
                          color: rendement >= 100 ? '#22c55e' : 
                                 rendement >= 80 ? '#f59e0b' : '#ef4444' 
                        }}
                      >
                        {rendement}%
                      </div>
                      <div className="w-full h-2 bg-[var(--bg3)] rounded-full mt-1">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(rendement, 100)}%`,
                            background: rendement >= 100 ? '#22c55e' : 
                                       rendement >= 80 ? '#f59e0b' : '#ef4444'
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Entry Modal */}
      {showEntryModal && (
        <div className="panel-overlay" onClick={() => setShowEntryModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">
                {editingEntry ? 'Modifier la saisie' : 'Nouvelle saisie VT'}
              </div>
              <button className="panel-close" onClick={() => setShowEntryModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Technicien *</label>
                <select
                  className="input w-full"
                  value={formData.technicien_id}
                  onChange={e => setFormData({ ...formData, technicien_id: e.target.value })}
                  data-testid="vt-select-tech"
                >
                  <option value="">Sélectionner...</option>
                  {techniciens.filter(t => !t.role || t.role === 'technicien' || t.role === 'Atelier').map(t => (
                    <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Date *</label>
                <input
                  type="date"
                  className="input w-full"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  data-testid="vt-input-date"
                />
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">
                  VT Réalisés * ({vtToHM(formData.vt_realises)})
                </label>
                <input
                  type="number"
                  className="input w-full"
                  min={1}
                  value={formData.vt_realises}
                  onChange={e => setFormData({ ...formData, vt_realises: parseInt(e.target.value) || 0 })}
                  data-testid="vt-input-vt"
                />
                <div className="flex gap-2 mt-2">
                  {[6, 12, 24, 48, 96].map(vt => (
                    <button
                      key={vt}
                      type="button"
                      className={`flex-1 py-1 px-2 rounded text-xs ${formData.vt_realises === vt ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg3)]'}`}
                      onClick={() => setFormData({ ...formData, vt_realises: vt })}
                    >
                      {vt} VT
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Ordre de Travail (optionnel)</label>
                <select
                  className="input w-full"
                  value={formData.ordre_travail_id}
                  onChange={e => setFormData({ ...formData, ordre_travail_id: e.target.value })}
                  data-testid="vt-select-ot"
                >
                  <option value="">Sans OT spécifique</option>
                  {ots.map(ot => (
                    <option key={ot.id} value={ot.id}>{ot.reference}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Description (optionnel)</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  placeholder="Travaux effectués..."
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  data-testid="vt-input-desc"
                />
              </div>
            </div>

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={handleSaveEntry}
              disabled={!formData.technicien_id || !formData.date || formData.vt_realises <= 0}
              data-testid="vt-save-btn"
            >
              <Check size={14} /> {editingEntry ? 'Enregistrer' : 'Ajouter la saisie'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaisieVT;
